<template>
  <b-modal
    id="UserDetailModal"
    ref="modal"
    :title="modalTitle"
    size="xl"
    centered
    scrollable
    hide-footer
  >
    <div v-if="isLoading" class="text-center py-5">
      <b-spinner />
      <div class="mt-2 text-muted">กำลังโหลดข้อมูล...</div>
    </div>

    <div v-else-if="!user" class="text-center py-5 text-muted">
      ไม่พบผู้ใช้
    </div>

    <template v-else>
      <!-- Identity sub-line (no UID, no avatar) -->
      <div class="identity-strip mb-3">
        <span v-if="branchCount" class="pill">{{ branchCount }} สาขา</span>
        <span v-if="centralCount" class="pill">{{ centralCount }} บทบาทระดับคลินิก</span>
        <span v-if="!branchCount && !centralCount" class="text-muted small">
          ยังไม่มีบทบาทในระบบ
        </span>
      </div>

      <!-- Primary actions surfaced (not buried in a ⋯ menu) -->
      <div class="action-bar mb-3">
        <b-button-group>
          <b-button
            variant="primary"
            :disabled="assignableBranchRoles.length === 0"
            @click="openMultiBranchDialog"
          >
            <i class="fas fa-layer-group mr-1"></i>
            มอบบทบาทข้ามสาขา
          </b-button>
          <b-button
            v-if="canAssignAnyCentral"
            variant="outline-primary"
            @click="openAssignCentralDialog"
          >
            <i class="fas fa-user-shield mr-1"></i>
            มอบบทบาทระดับคลินิก
          </b-button>
        </b-button-group>
        <small class="text-muted ml-auto">
          <i class="fas fa-info-circle mr-1"></i>การกระทำทั้งหมดต้องระบุเหตุผล
        </small>
      </div>

      <b-tabs content-class="mt-3">
        <b-tab active>
          <template #title>
            บทบาทในแต่ละสาขา <b-badge pill variant="light">{{ branchCount }}</b-badge>
          </template>

          <div
            v-if="!user.branchRoles || user.branchRoles.length === 0"
            class="empty-card"
          >
            ไม่มีบทบาทในสาขาใด · ใช้ "มอบบทบาทข้ามสาขา" เพื่อเพิ่มหลายสาขาพร้อมกัน
          </div>

          <div v-else class="branch-cards">
            <div
              v-for="(b, i) in user.branchRoles"
              :key="b.branchId"
              class="branch-card"
            >
              <span class="branch-bar" :style="{ background: barColor(i) }"></span>
              <div class="branch-card-main">
                <div class="d-flex justify-content-between align-items-start">
                  <div class="branch-title">{{ b.branchName }}</div>
                  <b-button
                    v-if="canAssignBranch(b.branchId)"
                    variant="link"
                    size="sm"
                    class="p-0 add-role-btn"
                    @click="openAssignBranchDialog(b)"
                  >
                    <i class="fas fa-plus mr-1"></i>บทบาท
                  </b-button>
                </div>
                <div class="d-flex flex-wrap mt-2" style="gap: 0.4rem">
                  <b-badge
                    v-for="rk in b.roleKeys"
                    :key="rk"
                    variant="info"
                    class="role-chip"
                  >
                    {{ roleLabel(rk) }}
                    <button
                      v-if="canRemoveBranch(b.branchId)"
                      type="button"
                      class="chip-x"
                      @click="openRemoveBranchDialog(b, rk)"
                    >
                      <i class="fas fa-times"></i>
                    </button>
                  </b-badge>
                  <span v-if="!b.roleKeys || b.roleKeys.length === 0" class="text-muted small">
                    ยังไม่มีบทบาท
                  </span>
                </div>
              </div>
            </div>

            <div class="empty-card subtle">
              + เพิ่มในสาขาอื่น · ใช้ "มอบบทบาทข้ามสาขา" สำหรับหลายสาขาพร้อมกัน
            </div>
          </div>
        </b-tab>

        <b-tab>
          <template #title>
            บทบาทระดับคลินิก <b-badge pill variant="light">{{ centralCount }}</b-badge>
          </template>
          <b-card no-body>
            <div
              v-if="!user.centralRoles || user.centralRoles.length === 0"
              class="p-4 text-muted text-center"
            >
              ไม่มีบทบาทระดับคลินิก
            </div>
            <b-table
              v-else
              :items="user.centralRoles"
              :fields="centralFields"
              hover
              striped
            >
              <template #cell(role)="row">
                <b-badge variant="warning">
                  {{ roleLabel(row.item.roleKey) }}
                </b-badge>
              </template>
              <template #cell(scope)="row">
                <span v-if="row.item.roleKey === 'RDM'">
                  <span v-if="row.item.branchIds && row.item.branchIds.length">
                    {{ row.item.branchIds.length }} สาขา
                  </span>
                  <span v-else class="text-muted">— (ยังไม่กำหนด)</span>
                </span>
                <span v-else class="text-muted">ทั้งคลินิก</span>
              </template>
              <template #cell(actions)="row">
                <b-button-group size="sm">
                  <b-button
                    v-if="row.item.roleKey === 'RDM' && canAssignRdm"
                    variant="outline-primary"
                    @click="openEditRdmDialog(row.item)"
                  >
                    <i class="fas fa-edit"></i>
                    ขอบเขต
                  </b-button>
                  <b-button
                    v-if="canRemoveCentral"
                    variant="outline-danger"
                    @click="openRemoveCentralDialog(row.item)"
                  >
                    <i class="fas fa-times"></i>
                    ถอด
                  </b-button>
                </b-button-group>
              </template>
            </b-table>
          </b-card>
        </b-tab>

        <b-tab lazy @click="loadAuditLogs">
          <template #title>
            ประวัติการเปลี่ยนแปลง
            <b-badge pill variant="light">{{ auditLogs.length }}</b-badge>
          </template>

          <div v-if="auditLoading" class="p-4 text-center">
            <b-spinner small />
          </div>
          <div v-else-if="auditLogs.length === 0" class="empty-card">
            ไม่มีประวัติ
          </div>
          <AuditTimeline v-else :logs="auditLogs" />
        </b-tab>
      </b-tabs>
    </template>

    <AssignRoleDialog ref="AssignRoleDialog" @assigned="onChanged" />
    <RemoveRoleDialog ref="RemoveRoleDialog" @removed="onChanged" />
    <MultiBranchAssignDialog ref="MultiBranchAssignDialog" @assigned="onChanged" />
    <EditRdmScopeDialog ref="EditRdmScopeDialog" @scope-updated="onChanged" />
  </b-modal>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import AssignRoleDialog from "@/components/user-mgmt/AssignRoleDialog.vue";
import RemoveRoleDialog from "@/components/user-mgmt/RemoveRoleDialog.vue";
import MultiBranchAssignDialog from "@/components/user-mgmt/MultiBranchAssignDialog.vue";
import EditRdmScopeDialog from "@/components/user-mgmt/EditRdmScopeDialog.vue";
import AuditTimeline from "@/components/user-mgmt/AuditTimeline.vue";
import { ROLE_LABELS_TH } from "@/constants/roles";

// Left-bar accent colors cycled across the per-branch cards.
const BAR_COLORS = ["#2f7a4d", "#2f5b8c", "#b8860b", "#7d5ba6", "#c5392e"];

export default {
  name: "UserDetailModal",
  components: {
    AssignRoleDialog,
    RemoveRoleDialog,
    MultiBranchAssignDialog,
    EditRdmScopeDialog,
    AuditTimeline,
  },
  data() {
    return {
      clinicUrl: null,
      uid: null,
      user: null,
      isLoading: false,
      auditLogs: [],
      auditLoading: false,
      branches: [],
      centralFields: [
        { key: "role", label: "บทบาท" },
        { key: "scope", label: "ขอบเขต" },
        { key: "actions", label: "", thStyle: { width: "180px" } },
      ],
    };
  },
  computed: {
    ...mapGetters({
      assignableRolesGetter: "moduleUser/assignableRoles",
      can: "moduleUser/can",
      branchInfo: "moduleUser/getBranchInfo",
    }),
    modalTitle() {
      return this.user
        ? `${this.user.firstNameTh} ${this.user.lastNameTh}`
        : "รายละเอียดผู้ใช้";
    },
    clinicId() {
      return this.branchInfo?.clinic?.id;
    },
    branchCount() {
      return this.user?.branchRoles?.length || 0;
    },
    centralCount() {
      return this.user?.centralRoles?.length || 0;
    },
    assignableBranchRoles() {
      return this.assignableRolesGetter("branch");
    },
    canAssignAnyCentral() {
      const keys = this.assignableRolesGetter("clinic");
      return keys.length > 0;
    },
    canAssignRdm() {
      return this.can("user.assign.RDM", { clinicId: this.clinicId });
    },
    canRemoveCentral() {
      return this.can("user.remove.clinic", { clinicId: this.clinicId });
    },
  },
  methods: {
    // Opens the modal for a given user. Resets state so the previously viewed
    // user doesn't flash before the new data loads.
    async show({ clinicUrl, uid }) {
      this.clinicUrl = clinicUrl;
      this.uid = uid;
      this.user = null;
      this.auditLogs = [];
      this.branches = [];
      this.$bvModal.show("UserDetailModal");
      await this.ensurePermissionContext();
      await this.refresh();
    },
    hide() {
      this.$bvModal.hide("UserDetailModal");
    },
    roleLabel(rk) {
      return ROLE_LABELS_TH[rk] || rk;
    },
    barColor(i) {
      return BAR_COLORS[i % BAR_COLORS.length];
    },
    canRemoveBranch(branchId) {
      return this.can("user.remove.branch", {
        clinicId: this.clinicId,
        branchId,
      });
    },
    // Whether the operator can add ANY branch role in this branch (drives the
    // per-card "+ บทบาท" button).
    canAssignBranch(branchId) {
      return this.assignableBranchRoles.some(rk =>
        this.can(`user.assign.${rk}`, { clinicId: this.clinicId, branchId })
      );
    },
    async ensurePermissionContext() {
      // ensurePermissionContextFor handles its own cache check by clinicURL —
      // re-loads only when the active context belongs to a different clinic.
      try {
        await this.$store.dispatch("moduleUser/ensurePermissionContextFor", {
          clinicURL: this.clinicUrl,
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("loadPermissionContext failed", err);
      }
    },
    // Re-fetch after a sub-dialog change, and tell the parent list to refresh.
    onChanged() {
      this.refresh();
      this.$emit("updated");
    },
    async refresh() {
      this.isLoading = true;
      try {
        const [userRes] = await Promise.all([
          axios.get(`/api/userMgmt/${this.clinicUrl}/users/${this.uid}`),
          this.loadBranches(),
        ]);
        this.user = userRes.data || null;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to load user detail", err);
        this.user = null;
      } finally {
        this.isLoading = false;
      }
      this.loadAuditLogs();
    },
    async loadAuditLogs() {
      this.auditLoading = true;
      try {
        const res = await axios.get(`/api/userMgmt/${this.clinicUrl}/audit-log`, {
          params: { targetUid: this.uid, limit: 50 },
        });
        const data = res.data || {};
        this.auditLogs = Array.isArray(data.logs) ? data.logs : [];
      } catch (err) {
        // Audit may 403 for users with only branch access — that's fine.
        this.auditLogs = [];
      } finally {
        this.auditLoading = false;
      }
    },
    async loadBranches() {
      try {
        const res = await axios.get(`/api/userMgmt/${this.clinicUrl}/users`);
        const list = Array.isArray(res.data?.branches) ? res.data.branches : [];
        this.branches = list.map(b => ({
          id: b.id,
          name: b.name,
          code: b.code,
        }));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to load clinic branches", err);
        this.branches = [];
      }
    },
    openMultiBranchDialog() {
      this.$refs.MultiBranchAssignDialog.show({
        targetUser: this.user,
        clinicUrl: this.clinicUrl,
        branches: this.branches,
      });
    },
    openAssignCentralDialog() {
      this.$refs.AssignRoleDialog.show({
        scope: "clinic",
        targetUser: this.user,
        clinicUrl: this.clinicUrl,
      });
    },
    branchCodeFor(branchId) {
      return this.branches.find(b => b.id === branchId)?.code || null;
    },
    // Per-branch "+ บทบาท" — add another role to an existing branch membership.
    openAssignBranchDialog(branchRow) {
      const branchUrl = this.branchCodeFor(branchRow.branchId);
      if (!branchUrl) return;
      this.$refs.AssignRoleDialog.show({
        scope: "branch",
        targetUser: this.user,
        clinicUrl: this.clinicUrl,
        branchUrl,
        branchId: branchRow.branchId,
      });
    },
    openRemoveBranchDialog(branchRow, roleKey) {
      const branchUrl = this.branchCodeFor(branchRow.branchId);
      if (!branchUrl) return;
      this.$refs.RemoveRoleDialog.show({
        scope: "branch",
        targetUser: this.user,
        roleKey,
        clinicUrl: this.clinicUrl,
        branchUrl,
        branchLabel: branchRow.branchName,
      });
    },
    openEditRdmDialog(centralRow) {
      this.$refs.EditRdmScopeDialog.show({
        targetUser: this.user,
        clinicUrl: this.clinicUrl,
        branches: this.branches,
        currentBranchIds: centralRow.branchIds || [],
      });
    },
    openRemoveCentralDialog(centralRow) {
      this.$refs.RemoveRoleDialog.show({
        scope: "clinic",
        targetUser: this.user,
        roleKey: centralRow.roleKey,
        clinicUrl: this.clinicUrl,
      });
    },
  },
};
</script>

<style scoped>
.identity-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
}

.pill {
  font-size: 0.8125rem;
  padding: 0.15rem 0.6rem;
  border: 1px solid #dce1e8;
  border-radius: 999px;
  background: #f6f8fb;
  color: #495057;
}

.action-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.empty-card {
  border: 2px dashed #ced4da;
  border-radius: 0.5rem;
  padding: 1rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.9rem;
}

.empty-card.subtle {
  border-width: 1px;
  padding: 0.625rem;
  font-size: 0.85rem;
}

.branch-cards {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.branch-card {
  display: flex;
  border: 1px solid #e3e8ef;
  border-radius: 0.5rem;
  overflow: hidden;
  background: #fff;
}

.branch-bar {
  flex: 0 0 5px;
}

.branch-card-main {
  flex: 1 1 auto;
  padding: 0.625rem 0.875rem;
  min-width: 0;
}

.branch-title {
  font-size: 1.05rem;
  font-weight: 600;
}

.add-role-btn {
  white-space: nowrap;
}

.role-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding-right: 0.4rem;
  font-size: 0.85rem;
}

.chip-x {
  border: 0;
  background: transparent;
  color: #fff;
  padding: 0;
  line-height: 1;
  cursor: pointer;
}

.chip-x:hover {
  opacity: 0.7;
}
</style>
